import { isEmpty } from 'lodash';
import { type ReactNode, useCallback, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import type { Project } from '~/common/types';
import { homeMapStore } from '~/pages/homeViewV2/store';
import rootStore from '~/store';
import layerStore from '~/store2/layers';
import ProjectsLayer, { type ProjectClickEvent } from './ProjectsLayer';

export default function DelegatedAccessProjects() {
  const { currentOrganizationDetail } = useSnapshot(rootStore) as typeof rootStore;
  const { visibleLinkedOrganizationIds } = useSnapshot(layerStore) as typeof layerStore;

  const onCrossOrgProjectClick = useCallback(
    (e: ProjectClickEvent) => {
      const {
        lngLat: { lng, lat },
      } = e;
      const properties = (e.properties ?? {}) as Record<string, unknown>;
      const id = properties.id as string;
      const projectId = properties.projectId as string;
      const name = properties.name as string;
      const projectPhase = properties.projectPhase as string | undefined;
      const projectYear = properties.projectYear as string | undefined;
      const billingTier = properties.billingTier as Project['billingTier'] | undefined;
      const projectManagerName = properties.projectManagerName as string | undefined;

      let organizationName = '';
      if (!currentOrganizationDetail.isLoading) {
        const det = currentOrganizationDetail.linkedOrganizations?.find((org) => org.id === properties.organizationId);
        if (det) {
          organizationName = det.name;
        }
      }

      homeMapStore.setPopupParams({
        id,
        projectId,
        projectName: name,
        projectPhase: projectPhase ?? '',
        projectYear: projectYear ?? '',
        lngLat: [lng, lat],
        projectBillingTier: billingTier ?? 'draft',
        projectAddress: '',
        projectManager: projectManagerName ?? '',
        isCrossOrgProject: true,
        organizationName,
      });
    },
    [currentOrganizationDetail],
  );

  const delegatedAccessProjects = useMemo(() => {
    if (currentOrganizationDetail.isLoading) return null;

    const linkedOrganizations = currentOrganizationDetail.linkedOrganizations;
    if (isEmpty(linkedOrganizations)) return null;

    const layers: ReactNode[] = [];

    linkedOrganizations?.forEach((org) => {
      if (visibleLinkedOrganizationIds.has(org.id)) {
        layers.push(
          <ProjectsLayer
            key={`delegated-access-projects-${org.id}`}
            organizationId={org.id}
            showClusters={false}
            isUnderlay
            onProjectClick={onCrossOrgProjectClick}
          />,
        );
      }
    });

    return layers;
  }, [currentOrganizationDetail, visibleLinkedOrganizationIds, onCrossOrgProjectClick]);

  return <>{delegatedAccessProjects}</>;
}
